import { useLayoutEffect, useRef } from 'react';

/**
 * Masonry by independent columns (events-wall handoff §5): each card goes into whichever column
 * is currently shortest.
 *
 * Append-only. A card keeps the column it was first given, so posters loading (which change a
 * card's height) and the next page arriving never shuffle what you're looking at. "Shortest" is
 * the columns' real rendered heights from the last commit, plus estimates for anything placed
 * since that hasn't rendered yet — the handoff's "better, measure the real rendered heights".
 *
 * `resetKey` starts the layout over (a filter change, a new column count).
 */
export default function useMasonry(items, cols, estimate, resetKey, gap = 28, initial = null) {
  const state = useRef({ key: null, col: new Map(), rendered: new Set(), measured: [] });
  const colEls = useRef([]);
  // A back-nav hands in the assignment the list had when it was left, so the first paint puts
  // every card back in the column it was in — the saved scroll offset only means anything if
  // the layout under it is the same.
  const seed = useRef(initial);

  const key = `${cols}|${resetKey}`;
  if (state.current.key !== key) {
    const s0 = seed.current;
    const fits = !!(s0 && s0.cols === cols && s0.key === resetKey && s0.col);
    // Hold the seed until a render with the same column count uses it (the first render can
    // guess the width before it has been measured).
    if (fits) seed.current = null;
    state.current = { key, col: fits ? new Map(Object.entries(s0.col)) : new Map(), rendered: new Set(), measured: [] };
  }
  const st = state.current;

  const heights = Array.from({ length: cols }, (_, c) => st.measured[c] || 0);
  // Placed but not on screen yet: count them by estimate.
  for (const it of items) {
    const c = st.col.get(it.id);
    if (c != null && c < cols && !st.rendered.has(it.id)) heights[c] += estimate(it) + gap;
  }

  const columns = Array.from({ length: cols }, () => []);
  for (const it of items) {
    let c = st.col.get(it.id);
    if (c == null || c >= cols) {
      c = heights.indexOf(Math.min(...heights));
      st.col.set(it.id, c);
      heights[c] += estimate(it) + gap;
    }
    columns[c].push(it);
  }

  // After commit: record what's on screen and how tall each column really is.
  useLayoutEffect(() => {
    st.rendered = new Set(items.map((it) => it.id));
    st.measured = colEls.current.slice(0, cols).map((el) => {
      if (!el) return 0;
      const cards = Array.from(el.children).filter(child => !child.hasAttribute('data-masonry-placeholder'));
      return cards.reduce((height, card) => height + card.offsetHeight, 0) + Math.max(0, cards.length - 1) * gap;
    });
  });

  const colRef = (c) => (el) => { colEls.current[c] = el; };
  /** The assignment as a plain object, for the back-nav cache. */
  const snapshot = () => ({ cols, key: resetKey, col: Object.fromEntries(st.col) });
  return { columns, colRef, snapshot };
}
